<script lang="ts" setup>
import { LOBBY_GAME_URLS } from '@kira-dancer/nuxt-common/constants'
import { DepositRouteEnum, AccountRouteEnum, RoutesEnum } from '~/enums'
import { useCustomFavoriteGame } from '~/composables/account/use-custom-favorite-game'
import {
  useGameToastStore,
  usePromotionStore,
  useModalsStore,
} from '#lib/stores'

const { maintainStartTime: startTime, maintainEndTime: endTime } =
  useGameToast()

const gameToastStore = useGameToastStore()
const {
  isShowMaintainAthenaGames,
  isShowNotEnoughBalance,
  isShowMaintainProvider,
  isShowMaintainGame,
  isShowCommingSoon,
  isShowPromotionPrevent,
} = storeToRefs(gameToastStore)

const { isOpenAlertFavoriteGame, statusFavorite } = useCustomFavoriteGame()
const promotionStore = usePromotionStore()
const setOpenWithdrawErrorModal = promotionStore.setOpenWithdrawErrorModal
const {
  isOpenWithdrawErrorModal,
  isOpenCancelPromotionModal,
  isOpenConfirmCancelPromotionModal,
} = storeToRefs(promotionStore)
const { navigate } = useNavigate()
const { lastGame, playGame } = useGames()
const router = useRouter()
const props = defineProps({
  expiredClass: {
    type: String,
    default: '',
    required: false,
  },
})

const { openModalConfirmCancelPromotion, onCancelPromotion } =
  useCancelPromotion()

const { openAuthModal } = useAuth()
const modalStore = useModalsStore()
const { isOpenExpiredModal } = storeToRefs(modalStore)

const isShowCancelPromotionError = ref(false)
const showSuccess = ref(false)
const cancelPromotion = async () => {
  try {
    await onCancelPromotion()
    showSuccess.value = true
  } catch {
    isShowCancelPromotionError.value = true
  }
}

const playTrialGame = () => {
  if (lastGame.value) {
    const gameData = {
      ...lastGame.value,
      moneyRequired: false,
    }

    if (gameData.link) {
      navigate(gameData.link, gameData.loginRequired, gameData.newTab)
      return
    } else {
      playGame(gameData)
    }
  }
}

watch(isShowMaintainGame, (value) => {
  if (!value && !lastGame) {
    const { history } = router.options
    if (history.state?.back) {
      router.back()
      return
    }
    navigateTo(RoutesEnum.Home)
  }
})
</script>

<template>
  <div>
    <BaseConfirm
      v-model="isShowCommingSoon"
      title="Sắp ra mắt"
      description="Game này sắp ra mắt. Vui lòng chọn game khác"
      image="/images/popup/failed.svg"
      cancel-label="Trang chủ"
      confirm-label="Sảnh game"
      @on-confirm="navigate(LOBBY_GAME_URLS.ALL_GAME)"
      @on-cancel="navigate(RoutesEnum.Home)"
    />
    <BaseConfirm
      v-model="isShowNotEnoughBalance"
      title="Nạp tiền vào ví"
      :description="`Số tiền quý khách không đủ, \n xin vui lòng thêm tiền vào tài khoản!`"
      image="/images/popup/failed.svg"
      cancel-label="Khám phá game"
      confirm-label="Nạp tiền ngay"
      @on-cancel="playTrialGame"
      @on-confirm="navigate(DepositRouteEnum.QRPay)"
    />
    <BaseConfirm
      v-model="isShowMaintainProvider"
      title="Nhà Cung Cấp Đang Bảo Trì"
      description="Nhà cung cấp này hiện đang được bảo trì. Vui lòng chọn nhà cung cấp khác"
      image="/images/popup/failed.svg"
      cancel-label="Trang chủ"
      confirm-label="Sảnh game"
      @on-confirm="navigate(LOBBY_GAME_URLS.ALL_GAME)"
      @on-cancel="navigate(RoutesEnum.Home)"
    />
    <BaseConfirm
      v-model="isShowMaintainAthenaGames"
      title="Game Đang Bảo Trì"
      :description="`Thời gian bảo trì từ <strong>${startTime}</strong> đến <strong>${endTime}</strong>. Vui lòng quay lại sau hoặc chọn game khác.`"
      image="/images/popup/ic-game-maintenance.svg"
      cancel-label="Trang chủ"
      confirm-label="Sảnh game"
      @on-confirm="navigate(LOBBY_GAME_URLS.ALL_GAME)"
      @on-cancel="navigate(RoutesEnum.Home)"
    />
    <BaseConfirm
      v-model="isShowMaintainGame"
      title="Bảo Trì"
      image="/images/popup/failed.svg"
      confirm-label="Đóng"
    >
      <template #description>
        <div>
          <span>Game này hiện đang được bảo trì. </span>
          <br />
          <span>Vui lòng chọn game khác.</span>
        </div>
      </template></BaseConfirm
    >
    <BaseConfirm
      v-model="isOpenExpiredModal"
      title="Phiên đăng nhập hết hạn"
      description="Vui lòng đăng nhập lại để tiếp tục trải nghiệm"
      image="/images/popup/logout-expired.svg"
      confirm-label="Đăng nhập lại"
      :custom-class="props.expiredClass"
      @on-confirm="openAuthModal"
    />
    <BaseConfirm
      v-model="isShowPromotionPrevent"
      title="Thông báo"
      image="/images/popup/failed.svg"
      cancel-label="Bỏ qua"
      confirm-label="Xem khuyến mãi"
      @on-confirm="navigate(AccountRouteEnum.MyPromotion)"
    >
      <template #description>
        <span>
          Bạn đang
          <span class="base-modal__description--highlight"
            >sử dụng gói khuyến mãi</span
          >, nên không thể
          <span class="base-modal__description--highlight"
            >truy cập vào game này</span
          >. Xin vui lòng kiểm tra lại!</span
        >
      </template></BaseConfirm
    >
    <BaseConfirm
      v-model="isOpenConfirmCancelPromotionModal"
      title="Hủy khuyến mãi"
      image="/images/popup/cancel-promotion.png"
      cancel-label="Bỏ qua"
      confirm-label="Hủy khuyến mãi"
      @on-confirm="cancelPromotion"
    >
      <template #description>
        <span>
          Khi bấm Hủy khuyến mãi, tài khoản của quý khách sẽ reset còn
          <span class="base-modal__description--highlight">0 K</span> (Bao gồm
          tiền Nạp & Khuyến mãi).</span
        >
      </template>
    </BaseConfirm>
    <BaseConfirm
      v-model="isOpenCancelPromotionModal"
      title="THÔNG BÁO"
      description="Tạo phiếu nạp tiền sẽ yêu cầu việc hủy chương trình khuyến mãi hiện tại. Bạn có muốn tiếp tục?"
      image="/images/popup/cancel-promotion.png"
      cancel-label="Bỏ qua"
      confirm-label="Đồng ý"
      @on-confirm="openModalConfirmCancelPromotion"
    />
    <BaseConfirm
      v-model="showSuccess"
      :timeout="2000"
      hide-close-button
      title="Thành công"
      description="Hủy khuyến mãi thành công."
      image="/images/popup/success.svg"
    />
    <BaseConfirm
      v-model="isShowCancelPromotionError"
      title="Thất bại"
      description="Quý khách không thể hủy khuyến mãi khi vé cược chưa kết thúc."
      image="/images/popup/failed.svg"
      cancel-label="Trang chủ"
      confirm-label="Xem khuyến mãi"
      @on-confirm="navigate(AccountRouteEnum.MyPromotion)"
      @on-cancel="navigate(RoutesEnum.Home)"
    />
    <BaseConfirm
      v-model="isOpenWithdrawErrorModal"
      title="Thất bại"
      description="Bạn chưa hoàn thành đủ vòng cược để rút tiền."
      image="/images/popup/failed.svg"
      confirm-label="Xác nhận"
      @on-confirm="setOpenWithdrawErrorModal(false)"
    />
    <BaseWarningMessage
      v-if="isOpenAlertFavoriteGame"
      icon-alert="icon icon-heart-full"
      :class-name="`favorite-message ${statusFavorite.class}`"
      :message="statusFavorite.message"
    />
  </div>
</template>
<style scoped></style>
